import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ExposureEnum } from '@reach/shared-types/schema-mappings';
import { Loader2, X } from 'lucide-react';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { jsonPathToFieldName } from '../../components/schemaFieldRemoval';
import { DialogBackdrop } from '../../components/ui/dialog-backdrop';
import {
  partnerApi,
  type ApiError,
  type CustomEventSchema,
  type FieldToExpose,
  type SchemaDefinition,
  type SchemaMappings,
} from '../../lib/api';
import { schemaDefinitionKeys } from '../../lib/schema-definition-query-keys';
import { LookupSchemaMappingSelect } from './LookupSchemaMappingSelect';
import { LookupSchemaReturnFieldRow } from './LookupSchemaReturnFieldRow';
import type { LookupReturnFieldState, LookupSchemaFieldOption } from './lookup-schema-setup-types';
import { schemaFieldsToExpose } from './lookup-tool-model';
import styles from './LookupTool.module.css';

interface LookupSchemaSetupDialogProps {
  schema: SchemaDefinition;
  mappings: SchemaMappings;
  onClose: () => void;
  onSaved: () => void;
}

const normalizedFieldName = (value: string): string => value.replace(/[^a-z0-9]/gi, '').toLowerCase();

const humanizeFieldName = (value: string): string => {
  const words = value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
  return words ? `${words.charAt(0).toUpperCase()}${words.slice(1)}` : value;
};

const schemaFieldOptions = (schema: SchemaDefinition): LookupSchemaFieldOption[] => {
  const properties =
    schema.schema.properties && typeof schema.schema.properties === 'object'
      ? (schema.schema.properties as Record<string, unknown>)
      : {};

  return Object.entries(properties).map(([name, definition]) => {
    const field =
      definition && typeof definition === 'object' ? (definition as Record<string, unknown>) : {};
    let type = 'field';
    if (typeof field.type === 'string') type = field.type;
    if (typeof field.format === 'string') type = field.format;
    return {
      name,
      type,
      description: typeof field.description === 'string' ? field.description : undefined,
    };
  });
};

const buildReturnFieldState = (
  fields: LookupSchemaFieldOption[],
  currentFields: FieldToExpose[],
): Record<string, LookupReturnFieldState> => {
  const currentBySource = new Map(
    currentFields.map(field => [jsonPathToFieldName(field.jsonPath), field] as const),
  );

  return Object.fromEntries(
    fields.map(field => {
      const current = currentBySource.get(field.name);
      return [
        field.name,
        {
          included: Boolean(current?.exposure.includes(ExposureEnum.AGENT_TOOLS)),
          returnName: current?.name || humanizeFieldName(field.name),
        },
      ];
    }),
  );
};

const buildUpdatedFields = ({
  fields,
  currentFields,
  returnFields,
}: {
  fields: LookupSchemaFieldOption[];
  currentFields: FieldToExpose[];
  returnFields: Record<string, LookupReturnFieldState>;
}): FieldToExpose[] => {
  const schemaFields = new Map(fields.map(field => [field.name, field]));
  const existingSources = new Set(currentFields.map(field => jsonPathToFieldName(field.jsonPath)));
  const updatedExisting = currentFields.flatMap(field => {
    const fieldName = jsonPathToFieldName(field.jsonPath);
    const schemaField = schemaFields.get(fieldName);
    const selection = returnFields[fieldName];
    if (!schemaField || !selection) return [field];

    const exposure: FieldToExpose['exposure'] = field.exposure.filter(
      value => value !== ExposureEnum.AGENT_TOOLS,
    );
    if (selection.included) exposure.push(ExposureEnum.AGENT_TOOLS);
    if (exposure.length === 0) return [];

    return [
      {
        ...field,
        name: selection.included ? selection.returnName.trim() : field.name,
        exposure,
      },
    ];
  });
  const newFields = fields
    .filter(field => !existingSources.has(field.name) && returnFields[field.name]?.included)
    .map(field => ({
      name: returnFields[field.name]?.returnName.trim() || humanizeFieldName(field.name),
      description: field.description || '',
      jsonPath: `.${field.name}`,
      exposure: [ExposureEnum.AGENT_TOOLS],
    }));

  return [...updatedExisting, ...newFields];
};

const matchingField = (options: LookupSchemaFieldOption[], preferredNames: string[]): string => {
  const normalizedPreferences = preferredNames.map(normalizedFieldName);
  return (
    options.find(option => normalizedPreferences.includes(normalizedFieldName(option.name)))?.name ?? ''
  );
};

const defaultDateField = (options: LookupSchemaFieldOption[]): string =>
  matchingField(options, ['createdAt', 'created_at', 'date', 'updatedAt', 'updated_at']) ||
  options.find(option => option.type === 'date-time' || option.type === 'date')?.name ||
  '';

const existingField = (options: LookupSchemaFieldOption[], fieldName: string | undefined): string =>
  fieldName && options.some(option => option.name === fieldName) ? fieldName : '';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const countStringIds = (value: unknown): number =>
  Array.isArray(value) ? value.filter(item => typeof item === 'string').length : 0;

const structuredConflictDetail = (conflict: unknown): string | null => {
  if (!isRecord(conflict)) return null;

  if (typeof conflict.toolName === 'string') {
    if (conflict.reason === 'user_id_invalid') {
      return `Tool "${conflict.toolName}" uses the current contact relationship. Update or deactivate it first.`;
    }
    if (conflict.reason === 'schema_removed') {
      return `Tool "${conflict.toolName}" uses this schema. Update or deactivate it first.`;
    }
    return `Tool "${conflict.toolName}" has an incompatible lookup configuration. Update or deactivate it first.`;
  }

  if (typeof conflict.fieldName === 'string') {
    const consumerCount =
      countStringIds(conflict.toolDefinitionIds) +
      countStringIds(conflict.segmentIds) +
      countStringIds(conflict.communicationGroupIds) +
      countStringIds(conflict.automationIds);
    const consumers =
      consumerCount > 0
        ? `${consumerCount} active consumer${consumerCount === 1 ? '' : 's'}`
        : 'an active consumer';
    return `Field "${conflict.fieldName}" is used by ${consumers}. Resolve that dependency before changing it.`;
  }

  return null;
};

const structuredConflictMessage = (data: unknown): string | null => {
  if (!isRecord(data) || !isRecord(data.error)) return null;

  const error = data.error;
  const details = [
    ...(Array.isArray(error.conflicts) ? error.conflicts : []),
    ...(Array.isArray(error.fieldsToExposeConflicts) ? error.fieldsToExposeConflicts : []),
  ]
    .map(structuredConflictDetail)
    .filter((detail): detail is string => detail !== null);
  const message = typeof error.message === 'string' ? error.message : null;

  return [message, ...details].filter(Boolean).join(' ') || null;
};

const setupErrorMessage = (error: ApiError | null): string => {
  if (!error) return '';
  const responseData = error.response?.data;
  const details = responseData?.details;
  if (details?.length) return details.join(' ');
  const conflictMessage = structuredConflictMessage(responseData?.data);
  if (conflictMessage) return conflictMessage;
  return responseData?.message || responseData?.error || error.message;
};

const upsertCustomMapping = (mappings: SchemaMappings, mapping: CustomEventSchema): SchemaMappings => {
  const existing = mappings.customSchema ?? [];
  const existingIndex = existing.findIndex(entry => entry.schemaId === mapping.schemaId);
  let customSchema = [...existing, mapping];
  if (existingIndex !== -1) {
    customSchema = existing.map((entry, index) => (index === existingIndex ? mapping : entry));
  }

  return {
    ...mappings,
    type: 'combined_schema',
    customSchema,
  };
};

export const LookupSchemaSetupDialog = ({
  schema,
  mappings,
  onClose,
  onSaved,
}: LookupSchemaSetupDialogProps) => {
  const queryClient = useQueryClient();
  const titleId = useId();
  const formId = useId();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const recordIdFieldId = useId();
  const customerIdFieldId = useId();
  const dateFieldId = useId();
  const fields = schemaFieldOptions(schema);
  const currentFields = schemaFieldsToExpose(schema);
  const existingMapping = mappings.customSchema?.find(mapping => mapping.schemaId === schema.id);
  const [recordIdField, setRecordIdField] = useState(
    existingField(fields, existingMapping?.id_field) ||
      matchingField(fields, [schema.externalIdField || schema.external_id_field || '', 'id']),
  );
  const [customerIdField, setCustomerIdField] = useState(
    existingField(fields, existingMapping?.userId) ||
      matchingField(fields, ['customerId', 'customer_id', 'userId', 'user_id']),
  );
  const [dateField, setDateField] = useState(
    existingField(fields, existingMapping?.date) || defaultDateField(fields),
  );
  const [returnFields, setReturnFields] = useState(() => buildReturnFieldState(fields, currentFields));

  const updateMutation = useMutation<
    { updatedMappings: SchemaMappings; updatedSchema: SchemaDefinition },
    ApiError
  >({
    mutationFn: async () => {
      const nextMapping: CustomEventSchema = {
        ...existingMapping,
        type: 'custom_schema',
        schemaId: schema.id,
        id_field: recordIdField,
        userId: customerIdField,
        date: dateField || undefined,
      };
      const nextMappings = upsertCustomMapping(mappings, nextMapping);
      const nextFields = buildUpdatedFields({ fields, currentFields, returnFields });
      const updatedMappings = await partnerApi.updateSchemaMappings(nextMappings);
      const fieldsChanged = JSON.stringify(nextFields) !== JSON.stringify(currentFields);
      const updatedSchema = fieldsChanged
        ? await partnerApi.updateSchemaDefinition(schema.id, { fieldsToExpose: nextFields })
        : schema;
      return { updatedMappings, updatedSchema };
    },
    onSuccess: async ({ updatedMappings, updatedSchema }) => {
      queryClient.setQueryData(['schemaMappings'], updatedMappings);
      queryClient.setQueryData<SchemaDefinition[]>(schemaDefinitionKeys.list(), current =>
        current?.map(definition => (definition.id === updatedSchema.id ? updatedSchema : definition)),
      );
      queryClient.setQueryData(schemaDefinitionKeys.detail(updatedSchema.id), updatedSchema);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['schemaMappings'] }),
        queryClient.invalidateQueries({ queryKey: schemaDefinitionKeys.list() }),
        queryClient.invalidateQueries({ queryKey: ['availableSegmentFieldsWithTypes'] }),
      ]);
      onSaved();
    },
    // The mapping write can land before the field write fails, so refetch rather than
    // leave the dialog retrying against a snapshot the server has already moved past.
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: ['schemaMappings'] });
      void queryClient.invalidateQueries({ queryKey: schemaDefinitionKeys.list() });
    },
  });

  const selectedReturnFields = Object.values(returnFields).filter(field => field.included);
  const returnFieldsValid =
    selectedReturnFields.length > 0 && selectedReturnFields.every(field => field.returnName.trim());
  const canSave =
    recordIdField.length > 0 &&
    customerIdField.length > 0 &&
    returnFieldsValid &&
    !updateMutation.isPending;
  const title = `Set up ${schema.name}`;
  const errorMessage = setupErrorMessage(updateMutation.error);

  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeButtonRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('keydown', closeOnEscape);
      previousFocus?.focus();
    };
  }, [onClose]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (canSave) updateMutation.mutate();
  };

  return createPortal(
    <>
      <DialogBackdrop onClose={onClose} />
      <dialog
        className={styles.dialogShell}
        open
        aria-modal="true"
        aria-labelledby={titleId}
        onCancel={event => {
          event.preventDefault();
          onClose();
        }}>
        <section className={styles.dialogPanel}>
          <header className={styles.dialogHeader}>
            <div>
              <h2 id={titleId}>{title}</h2>
              <p>Connect records to contacts, then choose what this lookup is allowed to return.</p>
            </div>
            <button
              ref={closeButtonRef}
              type="button"
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Close">
              <X />
            </button>
          </header>
          <form id={formId} className={styles.setupForm} onSubmit={handleSubmit}>
            <fieldset className={styles.mappingFields}>
              <legend>1. Set the record mapping</legend>
              <p>
                Choose the fields that identify each record and its contact. A primary date is optional.
              </p>
              <LookupSchemaMappingSelect
                id={recordIdFieldId}
                label="Record ID"
                hint="Uniquely identifies each record."
                value={recordIdField}
                options={fields}
                onChange={setRecordIdField}
              />
              <LookupSchemaMappingSelect
                id={customerIdFieldId}
                label="Contact ID"
                hint="Matches the record to the contact using the tool."
                value={customerIdField}
                options={fields}
                onChange={setCustomerIdField}
              />
              <LookupSchemaMappingSelect
                id={dateFieldId}
                label="Record date (optional)"
                hint="The primary timestamp for the record, if it has one."
                value={dateField}
                options={fields}
                onChange={setDateField}
                optional
              />
            </fieldset>
            <fieldset className={styles.returnFields}>
              <legend>2. Choose returned fields</legend>
              <p>
                Select the fields Agentic Tools may return. The exposure name is the field label they
                receive and is shared with any other enabled exposure.
              </p>
              <aside className={styles.fieldExposureWarning}>
                Agentic Tools exposure is schema-wide: every lookup tool using this schema can return
                enabled fields. Only expose sensitive values, such as gate codes, when agents are allowed
                to disclose them.
              </aside>
              <ul>
                {fields.map(field => {
                  const selection = returnFields[field.name];
                  if (!selection) return null;
                  return (
                    <LookupSchemaReturnFieldRow
                      key={field.name}
                      field={field}
                      selection={selection}
                      onIncludedChange={included =>
                        setReturnFields(current => ({
                          ...current,
                          [field.name]: {
                            ...(current[field.name] ?? selection),
                            included,
                          },
                        }))
                      }
                      onReturnNameChange={returnName =>
                        setReturnFields(current => ({
                          ...current,
                          [field.name]: {
                            ...(current[field.name] ?? selection),
                            returnName,
                          },
                        }))
                      }
                    />
                  );
                })}
              </ul>
            </fieldset>
          </form>
          {/* Lives in the pinned footer, not the scrolling form, so a failed save is visible
              from wherever the field list is scrolled to. */}
          <footer className={styles.dialogFooter}>
            {errorMessage && (
              <p className={styles.setupError} role="alert">
                {errorMessage}
              </p>
            )}
            <button type="button" className={styles.cancelButton} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className={styles.dialogSaveButton} form={formId} disabled={!canSave}>
              {updateMutation.isPending && <Loader2 />}
              Save setup
            </button>
          </footer>
        </section>
      </dialog>
    </>,
    document.body,
  );
};
